import React, { useState, useEffect, useRef } from 'react';
import { Button, Icon, Tooltip, Tree } from '@scuf/common';

import './OrganisationHierarchy.scss';
import { pascalToCamelCase } from '../onboardingUtils';

import { consumeAPIs, IAPIPackage } from '../../commonUtils/APIContext';
import { useAuthService } from '../../../auth/hooks/useAuthService';
import {
  IOrganisationResponse,
  INodeResponse,
  INodeMetadataResponse,
  INodeMetadata,
  INodeMetadatum,
  ENode,
  INodeExportAuth,
  OnBoardingStatus,
} from '../onboardingModels';

import { SplitPanelUtil } from '../../commonUtils/SplitPanelUtil';
import OrganisationHierarchySidePanel, { IRootNodeUpdate } from './OrganisationHierarchySidePanel';
import {
  HierarchyTable,
  Column,
  TreeNode,
  TreeTableHeader
} from '../../commonUtils/HierarchyTable';

import toasts from '../../commonUtils/toasts';
import OrganisationHierarchyNewOrgSidePanel from './OrganisationHierarchyNewOrgSidePanel';
import i18next from '../../../i18next';
import { DataTable } from '@scuf/datatable';
import { useHistory } from 'react-router-dom';
import { update } from 'lodash';
import _ from 'lodash';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useSelector } from 'react-redux';
import { IOrganisationState } from '../../commonUtils/Redux/Global/globalShared';
import { formatCBFailedStatus } from '../../commonUtils/commonEnums';

export interface IOrganisationBasicData {
  organisationId: string;
  organisationName: string;
}

interface IOrganisationsList extends IAPIPackage {}

// Amount of rows ahead the lazy row load implementation will load ahead.
// too low will slighty slow scroll behaviour, too high is unnecessary.
const lazyRowBufferDistance = 5;

export const OrganisationHierarchy = (props: IOrganisationsList) => {
  // const [
  //   organisationData,
  //   isLoadingOrganisationData,
  //   errorOrganisationData,
  //   reFetchOrganisationData
  // ]: IUseFetch<IOrganisationResponse> = useFetch(
  //   `${props.baseURL}/api/RootOrganisations/${props.offering}`,
  //   props.authFetch
  // );
  const { hasGlobalPermission, baseURL, authFetch } = useAuthService();
  let history = useHistory();

  const flags = useFlags();

  const globalAbortControllerRef = useRef<AbortController>(
    new AbortController()
  );

  const { signal } = globalAbortControllerRef.current;

  const [organisationHierarchy, setOrganisationHierarchy] = useState<
    TreeNode[]
  >([]);

  const [organisationDataLoading, setOrganisationDataLoading] = useState<
    boolean
  >(false);

  const hasFileDownloadPermission = hasGlobalPermission('OrgManagement_OrgHierarchy_Export');

  const isDownloadTemplateForAnOrgEnabled = (data: any) => {    
    if(!flags.isOrgManagerDownloadEnabled) return false;
    const node = nodePriviliges.find((node: any) => node.id === data.key);
    return !!node?.isExportEnabled;
  }
 
  const fetchRootOrganisationData = () => {
    setOrganisationDataLoading(true);
    props
      .authFetch(`${props.baseURL}/api/RootOrganisations/${props.offering}`, {
        signal
      })
      .then(res => res.json() as Promise<IOrganisationResponse>)
      .then(rootOrgReq => {
        const orgHierarchyStructure: TreeNode[] = rootOrgReq.resources.map(
          org => ({
            key: org.id,
            children: [],
            leaf: org.childCount <= 0,
            data: { ...org, rootNodeId: org.id }
          })
        );

        setOrganisationHierarchy(orgHierarchyStructure);
      })
      .catch(e => {
        if (!signal.aborted) {
          console.error(`${i18next.t('hierarchyError')}: ${e}`);
          toasts.error(i18next.t('hierarchyError'));
        }
      })
      .finally(() => {
        setOrganisationDataLoading(false);
      });
  };

  const fetchChildrenAndInsert = (rootNodeId: string, parentId: string, isToUpdateHierarchy?: boolean ,rootNodeUpdate?:IRootNodeUpdate) => {
    // Block ui and show loading icon only when data is not present,
    // but still make call and update if there is some data there
    if(rootNodeUpdate?.isRootUpdate){

      const tempOrgIndex = nodeMetaData.findIndex(m => m.id === rootNodeId);
      if (tempOrgIndex !== -1) {
        rootNodeUpdate.updateData.map((updateData :any) => {
        var key= updateData["key"];
        var value= updateData["value"];
        if(key=="Currency"){
            let currencyindex= nodeMetaData[tempOrgIndex].data.findIndex(f=>f.key=="Currency") ;
            if(currencyindex!==-1){
              nodeMetaData[tempOrgIndex].data[currencyindex].value=value;
              setNodeMetadata(nodeMetaData);
            }
            else{
              nodeMetaData[tempOrgIndex].data.push({key:"Currency",value:value});
              setNodeMetadata(nodeMetaData);
            }
        }
        else if(key=="LegalConsent"){
            let legalConsentindex= nodeMetaData[tempOrgIndex].data.findIndex(f=>f.key.toUpperCase()=="LEGALCONSENT") ;
            if(legalConsentindex!==-1){
              nodeMetaData[tempOrgIndex].data[legalConsentindex].value=value;
              setNodeMetadata(nodeMetaData);
            }
            else{
              nodeMetaData[tempOrgIndex].data.push({key:"LegalConsent",value:value});
              setNodeMetadata(nodeMetaData);
            }
          }
      });
    }
  }

    setOrganisationDataLoading(true);
    props
      .authFetch(
        `${props.baseURL}/api/Organisations/${props.offering}/${rootNodeId}/${parentId}/Organisations`,
        {
          signal
        }
      )
      .then(res => res.json() as Promise<INodeResponse>)
      .then(childrenNodeResponse => {
        const childrenNodes: TreeNode[] = childrenNodeResponse.resources.map(
          node => ({
            key: node.id,
            children: [],
            leaf: node.childCount === 0,
            data: { ...node, rootNodeId: rootNodeId }
          })
        );        
        childrenNodes.forEach(node => {
          fetchMetaData(node.data.rootNodeId, node.data.id);
        });
        if (isToUpdateHierarchy) {
          updateToHierarchy(parentId, childrenNodes);
        } else 
        addToHierarchy(parentId, childrenNodes);
      })
      .catch(e => {
        if (!signal.aborted) {
          console.error(`${i18next.t('childNodesError')}: ${e}`);
          toasts.error(i18next.t('childNodesError'));
        }
      })
      .finally(() => {
        setOrganisationDataLoading(false);
      });
  };

  const updateToHierarchy = (targetId: string, children: Array<TreeNode>) => {
    if (organisationHierarchy) {
      setOrganisationHierarchy(prevState =>
        prevState.map(node => updateChildrenInHierarchy(targetId, node, children))
      );
    }
  }

  const UpdateMetaData = (hierarchy: TreeNode[]) => {
    return hierarchy.map(node => {
      const tableReadyMetadata = handleBadlyNamedProperties(
        node.data.metadata
      ).reduce(
        (ob, currentVal: any) => ({
          ...ob,
          [pascalToCamelCase(currentVal.name)]: currentVal.value
        }),
        {}
      );

      node.data = { ...node.data, ...tableReadyMetadata };

      if (node.children) {
        node.children = UpdateMetaData(node.children);
      }

      return node;
    });
  };

  const updateChildrenInHierarchy = (
    targetId: string,
    hierarchyNode: TreeNode,
    children: Array<TreeNode>
  ) => {
    if (hierarchyNode.key === targetId) {
      hierarchyNode.children = _.cloneDeep(UpdateMetaData(children));
      return hierarchyNode;
    } else if (hierarchyNode.children) {
      hierarchyNode.children = hierarchyNode.children.map(child =>
        updateChildrenInHierarchy(targetId, child, children)
      );
    }    
    return hierarchyNode;
  };

  const addToHierarchy = (targetId: string, children: Array<TreeNode>) => {
    if (organisationHierarchy) {
      setOrganisationHierarchy(prevState =>
        prevState.map(node => putChildrenInHierarchy(targetId, node, children))
      );
    }
  };

  const putChildrenInHierarchy = (
    targetId: string,
    hierarchyNode: TreeNode,
    children: Array<TreeNode>
  ) => {
    if (hierarchyNode.key === targetId) {
      // Check children arent different at immediate level
      const childCheck = children.map(targetChild =>
        hierarchyNode.children.find(
          sourceChild => sourceChild.key === targetChild.key
        )
      );

      if (childCheck.includes(undefined)) {
        hierarchyNode.children = children;
      }
    } else if (hierarchyNode.children) {
      hierarchyNode.children = hierarchyNode.children.map(child =>
        putChildrenInHierarchy(targetId, child, children)
      );
    }
    return hierarchyNode;
  };

  useEffect(() => {
    fetchRootOrganisationData();
    return () => {
      // This aborts ongoing requests on component unmount
      globalAbortControllerRef.current.abort();
    };
  }, []);

  const handleBadlyNamedProperties = (propertyArray: Array<INodeMetadatum>) => {
    interface IRemovalListItem {
      initial: string;
      transformed: string;
    }

    const removalList: IRemovalListItem[] = [
      {
        initial: 'SiteAddress',
        transformed: 'Address'
      }
    ];

    return propertyArray.map(propertyObj => {
      const foundMatch = removalList.find(
        removalItem => propertyObj.key === removalItem.initial
      );
      if (foundMatch) {
        return {
          key: foundMatch.transformed,
          value: propertyObj.value
        };
      } else {
        return propertyObj;
      }
    });
  };

  const injectMetaData = (hierarchy: TreeNode[]) => {
    return hierarchy.map(node => {
      const matchingMetaData = nodeMetaData.find(
        nodeMetaDatum => node.key === nodeMetaDatum.id
      );

      const nodeExportAuth = nodePriviliges.find(
        nodePrivilige => node.key === nodePrivilige.id
      );
      const tableReadyMetadata = matchingMetaData
        ? handleBadlyNamedProperties(matchingMetaData.data).reduce(
            (ob, currentVal) => ({
              ...ob,
              [pascalToCamelCase(currentVal.key)]: currentVal.value
            }),
            {}
          )
        : {};
      node.data = { ...node.data, ...tableReadyMetadata, isExportEnabled: !flags.isOrgManagerDownloadEnabled || hasFileDownloadPermission || nodeExportAuth?.isExportEnabled };
      if (node.children) {
        node.children = injectMetaData(node.children);
      }
      return node;
    });
  };

  const fetchMetaData = (rootNodeId: string, nodeId: string) => {
    props
      .authFetch(
        rootNodeId === nodeId
          ? `${props.baseURL}/api/v1/metadata/${props.offering}/${rootNodeId}`
          : `${props.baseURL}/api/v1/metadata/${props.offering}/${rootNodeId}/${nodeId}`,
        {
          signal
        }
      )
      .then(res => {
        if (res.ok) {
          return res;
        } else {
          throw new Error(i18next.t('hierarchyMetaError'));
        }
      })
      .then(res => res.json() as Promise<INodeMetadataResponse>)
      .then(metaData => {
        // console.log('Metadata: ', metaData);
        const metaDataObject: INodeMetadata = {
          id: nodeId,
          data: metaData
        };
        setNodeMetadata(prevMetadataState => [
          ...prevMetadataState,
          metaDataObject
        ]);
      })
      .catch(e => {
        if (!signal.aborted) {
          console.error(`${i18next.t('nodeMetaError')}: ${e}`);
        }
      });
  };

  const fetchPriviligesForOrganisation = (organisationId: string) => {
    props
      .authFetch(
        `${baseURL}/api/v1/users/orgs/${organisationId}/can-update-orghierarchy`
      )
      .then(res => {
        if (res.ok) {
          return res;
        } else {
          throw new Error(i18next.t('nodeMetaError'));
        }
      })
      .then(res => res.json() as Promise<boolean>)
      .then(isExportEnabled => {
        const nodePriviligesObject: INodeExportAuth = {
          id: organisationId,
          isExportEnabled: isExportEnabled
        };
        setNodePriviliges(prevData => [
          ...prevData,
          nodePriviligesObject
        ]);
      })
      .catch(e => console.error(`${i18next.t('nodeMetaError')}: ${e}`));
  };

  const [nodeMetaData, setNodeMetadata] = useState<INodeMetadata[]>([]);

  const [nodePriviliges, setNodePriviliges] = useState<INodeExportAuth[]>([]);

  const [selectedNode, setSelectedNode] = useState<TreeNode>();

  const [isCurrencyDefaultValue, setisCurrencyDefaultValue] = useState<boolean>(false);

  const [globalFilterQuery, setGlobalFilterQuery] = useState<string>();

  const filterHierarchy = (hierarchy: TreeNode[]) => {
    // Only doing top level right now
    if (globalFilterQuery) {
      return injectMetaData(organisationHierarchy).filter(node =>
        node.data.name.toLowerCase().includes(globalFilterQuery.toLowerCase())
      );
    } else {
      return hierarchy;
    }
  };

  const [newOrganisationOpenState, setNewOrganisationOpenState] = useState<
    boolean
  >(false);

  const [downloadTemplateLoading, setDownloadTemplateLoading] = useState(false);
  const [downloadOrgTemplateLoading, setDownloadOrgTemplateLoading] = useState(false);

  const newOrganisationHandler = () => {
    setSelectedNode(undefined);
    setNewOrganisationOpenState(true);
  };

  const refreshData = () => {
    fetchRootOrganisationData();
  };

  /**
   * A function to get any one value out of any list of headers, given the header name and the parameter name;
   * @param headers Header object to search in
   * @param headerName Header string to find in headers
   * @param subparameter String to match and return inside header parameters
   */
  const getSubparameterFromResponseHeaders = (
    headers: Headers,
    headerName: string,
    subparameter?: string
  ) => {
    const header = headers.get(headerName);
    if (header && subparameter) {
      const indexOfSubparam = header.indexOf(subparameter);
      if (indexOfSubparam !== -1) {
        const indexOfSubparamEnd = header.indexOf(';', indexOfSubparam);
        return header.slice(
          indexOfSubparam,
          indexOfSubparamEnd !== -1 ? indexOfSubparamEnd : undefined
        );
      } else {
        return null;
      }
    } else {
      return header;
    }
  };

  const removeSubparamNameFromHeaderSubparameterStr = (
    headerSubProp: string
  ) => {
    return headerSubProp.slice(headerSubProp.indexOf('=') + 1).replace(/"/g,'');
  };

  const onExpandRow = (rowExpanding: {
    originalEvent: Event;
    node: TreeNode;
  }): void => {
    setSelectedNode(undefined);
    if (rowExpanding.node.children.length === 0) {
      fetchChildrenAndInsert(
        rowExpanding.node.data.rootNodeId,
        rowExpanding.node.key
      );
    }
  };

  const downloadFile = (rowData: any): void => {
    if (!downloadOrgTemplateLoading) {
      let el = document.querySelector(`[id='${rowData.data.id}']`)
      if (el) {
        el.classList.add('loading')
      }
      setDownloadOrgTemplateLoading(true);
      authFetch(
          `${props.baseURL}/api/v1/organisations/LCBS/download/template?organisationId=${rowData.data.id}`,
          {
            signal
          }
        )
        .then(async res => {
          if (res.ok) {
            var file = window.URL.createObjectURL(await res.blob());
            const filenameParam = getSubparameterFromResponseHeaders(
              res.headers,
              'Content-Disposition',
              'filename'
            );
            const fileName = filenameParam
              ? removeSubparamNameFromHeaderSubparameterStr(
                  filenameParam
                )
              : undefined;
            const link = document.createElement('a');
            link.href = file;
            link.download = fileName ? fileName : 'Unknown';
            link.click();
            window.URL.revokeObjectURL(file);
          } else {
            throw new Error(i18next.t('templateError1'));
          }
        })
        .catch(e => {
          if (!signal.aborted) {
            console.error(`${i18next.t('templateError')}: ${e}`);
            setDownloadOrgTemplateLoading(false);
            if (el) {
              el.classList.remove('loading')
            }
            toasts.error(i18next.t('templateError1'));
          }
        })
        .finally(() => {
          setDownloadOrgTemplateLoading(false);
          if (el) {
            el.classList.remove('loading')
          }
        });
    }
  };

  const closeNewOrgSidepanel = () => {
    setNewOrganisationOpenState(false);
  };

  const closeNewOrgSidepanelAndRefresh = () => {
    closeNewOrgSidepanel();
    refreshData();
  };

  const closeSelectedTableNode = () => {
    setSelectedNode(undefined);
  };

  const closeSelectedTableNodeAndRefresh = () => {
    closeSelectedTableNode();
    refreshData();
  };

  const [
    indexToDisplayHierarchyUpTo,
    setIndexToDisplayHierarchyUpTo
  ] = useState(20);

  const lazyLoadData = (node: TreeNode) => {
    fetchMetaData(node.data.rootNodeId, node.data.id);
    if(flags.isOrgManagerDownloadEnabled && !hasFileDownloadPermission){
      fetchPriviligesForOrganisation(node.data.rootNodeId);
    }
    if (!globalFilterQuery) {
      const rowIndex = organisationHierarchy.findIndex(
        hierarchyNode => hierarchyNode.key === node.key
      );

      if (rowIndex + lazyRowBufferDistance > indexToDisplayHierarchyUpTo) {
        setIndexToDisplayHierarchyUpTo(rowIndex + lazyRowBufferDistance);
      }
    }
  };

  const limitHierarchyToIndex = (hierarchy: TreeNode[]) => {
    return hierarchy.slice(0, indexToDisplayHierarchyUpTo + 1);
  };

 
  return (
    <div className="organisations-hierarchy-view ">
      <div className="page-header-bar">
        <div className="page-header-bar-heading">
          {/* <Icon className="icon" root="common" name="globe" size="small" />  */}

          {i18next.t('allOrganisations')}
        </div>
        <div className="page-header-bar-controls padded-controls">
          <Button
            onClick={() => {
              if (!downloadTemplateLoading) {
                setDownloadTemplateLoading(true);
                authFetch(
                    `${props.baseURL}/api/v2/excelbatches/download/template/OrganisationTemplate`,
                    {
                      signal
                    }
                  )
                  .then(async res => {
                    if (res.ok) {
                      var file = window.URL.createObjectURL(await res.blob());
                      const filenameParam = getSubparameterFromResponseHeaders(
                        res.headers,
                        'Content-Disposition',
                        'filename'
                      );
                      const fileName = filenameParam
                        ? removeSubparamNameFromHeaderSubparameterStr(
                            filenameParam
                          )
                        : undefined;
                      const link = document.createElement('a');
                      link.href = file;
                      link.download = fileName ? fileName : 'Unknown';
                      link.click();
                      window.URL.revokeObjectURL(file);
                    } else {
                      throw new Error(i18next.t('templateError1'));
                    }
                  })
                  .catch(e => {
                    if (!signal.aborted) {
                      console.error(`${i18next.t('templateError')}: ${e}`);
                      setDownloadTemplateLoading(false);
                      toasts.error(i18next.t('templateError1'));
                    }
                  })
                  .finally(() => {
                    setDownloadTemplateLoading(false);
                  });
              }
            }}
            type="secondary"
            // disabled={downloadTemplateLoading}
            loading={downloadTemplateLoading}
            content="Loading"
            hidden={
              !hasGlobalPermission(
                'OrgManagement_OrgHierarchy_Import'
              )
            }
          >
            {i18next.t('downloadTemplate')}
            {/* {!downloadTemplateLoading
                  ? 'Download template'
                  : 'Downloading...'} */}
          </Button>

          <Button
            hidden={
              !hasGlobalPermission(
                'OrgManagement_OrgHierarchy_Import'
              )
            }
            onClick={() => newOrganisationHandler()}
          >
            {i18next.t('newOrg')}
          </Button>
        </div>
      </div>
      <div className="organisations-list-view-content page-content-container with-header">
        <SplitPanelUtil>
          <SplitPanelUtil.Table>
            <HierarchyTable
              // paginator={true}
              loading={organisationDataLoading}
              emptyMessage={i18next.t('noRecordFound')}
              // loading={true}

              value={filterHierarchy(
                limitHierarchyToIndex(injectMetaData(organisationHierarchy))
              )}
              // value={[]}
              // onRowClick={rowData => {
              //   setSelectedNode(rowData.node);
              // }}
              onExpand={onExpandRow}
              // resizableColumns={true}
              columnResizeMode="expand"
              lazy={true}
              onRowVisible={lazyLoadData}
              header={
                <>
                  <TreeTableHeader
                    selections={1}
                    showActionBar={false}
                    data={[]}
                    searchPlaceholder={i18next.t('searchFor')}
                    globalFilterValue={
                      globalFilterQuery ? globalFilterQuery : ''
                    }
                    onSearchChange={newValue => {
                      setGlobalFilterQuery(newValue);
                    }}
                    hideActionBar={() => {}}
                    search={true}
                    totalRecords={organisationHierarchy.length}
                  />
                  {/* Hierarchy builder, controlled by fake config, do NOT add config to production */}
                  {(window as any).ENV.TEST_HB_ENABLED ? (
                    <div
                      className="header-item-wrapper"
                      onClick={() => {
                        history.push(
                          '/app/feature/organisations/hierarchy-builder'
                        );
                      }}
                    >
                      <Icon root="common" size="medium" name="edit" />
                    </div>
                  ) : null}
                </>
              }
            >
              <Column
                style={{ fontWeight: 'bold' }}
                field="name"
                header={i18next.t('name')}
                expander
                // sortable
                onCellTextClicked={event => {
                  if (event.node.data.type === 'Site') {
                    let tempEventNode = event.node;
                    tempEventNode.data['latitudeAndLongitude'] = [
                      tempEventNode.data.latitude,
                      tempEventNode.data.longitude
                    ];
                    setSelectedNode(tempEventNode);
                  } else {
                    //if Org has not Currency property then add and set it to default value
                    if(event.node.data.type === ENode.Organisation){
                      setisCurrencyDefaultValue(!event.node.data?.currency ||
                         event.node.data.currency == ' '
                      );
                      if(!event.node.data?.currency){
                        event.node.data.currency = ' ';  
                      }   
                      if(!event.node.data?.LegalConsent){
                        event.node.data.legalConsent = 'No'
                      }    
                                 
                    }
                    setSelectedNode(event.node)
                  };
                }}
              />
              <Column
                field="type"
                header={i18next.t('type')}
                // sortable
              />
              <Column
                field="address"
                header={i18next.t('address')}
                style={{ width: '30%', wordBreak: 'normal' }}
                // sortable
              />
              <Column
                field="primaryContactName"
                header={i18next.t('contactName')}
                // sortable
              />
              <Column
                //field="commissioningStatus"
                header={i18next.t('status')}
                renderer={(cellData: any, rowData: any) => {                 
                  let status='';
                    if(rowData?.data?.type === ENode.Organisation && flags.showOnboardingStatus)
                    {    
                      const onboardingStatus = rowData?.data?.onboardingStatus as string;             
                      if(rowData?.data?.onboardingStatus){                        
                        if(onboardingStatus?.includes(OnBoardingStatus.Failed)){
                          const errorStatus=formatCBFailedStatus.find(f=>onboardingStatus.toLocaleLowerCase().includes(f.key.toLocaleLowerCase()))?.value ;
                          const tootipContent= errorStatus ? errorStatus as string : onboardingStatus.replace("Failed,", "").trim();
                        return <>
                                  <p style={{color:'#FF644C',display:'inline',paddingRight:'4px'}}>
                                      {OnBoardingStatus.ProvisioningFailed}  
                                  </p> 
                                  <Tooltip element={<Icon name="badge-info" root="building" /> } 
                                  content={tootipContent} position="bottom center" />
                                </>
                        }
                        else if(onboardingStatus?.includes(OnBoardingStatus.Completed)){
                          status = `${OnBoardingStatus.ProvisioningCompleted}`;
                        }
                        else{
                          status = `${OnBoardingStatus.UnderProvisioning}`;
                        }
                      }                   
                    }
                    else  if(rowData?.data?.type === ENode.Site)
                    { 
                      const onboardingStatus =  rowData?.data?.metadata?.find((m: any) => (m.name as string).toLocaleLowerCase() === 'OnboardingStatus'.toLocaleLowerCase())?.value as string; 
                    
                      if (rowData?.data?.commissioningStatus) {
                         return rowData?.data?.commissioningStatus;
                      }                      
                      else  if (onboardingStatus && flags.showOnboardingStatus) 
                      {
                          const errorStatus=formatCBFailedStatus.find(f=>onboardingStatus.toLocaleLowerCase().includes(f.key.toLocaleLowerCase()))?.value ;
                          const tootipContent= errorStatus ? errorStatus as string : onboardingStatus.replace("Failed,", "").trim();
                          if(onboardingStatus.toLocaleLowerCase().includes(OnBoardingStatus.Failed.toLocaleLowerCase())){
                            return (
                              <>
                                <p style={{ color: '#FF644C', display: 'inline', paddingRight: '4px' }}>
                                    {OnBoardingStatus.ProvisioningFailed}
                                </p>
                                <Tooltip element={<Icon name="badge-info" root="building" />} content={tootipContent} position="bottom center" />
                              </>
                              );
                          }
                          else  if (onboardingStatus.toLocaleLowerCase().includes(OnBoardingStatus.InProgress.toLocaleLowerCase())) {
                            status = `${OnBoardingStatus.UnderProvisioning}`;
                          }
                          else if (onboardingStatus.toLocaleLowerCase().includes(OnBoardingStatus.Completed.toLocaleLowerCase())) {
                            status = `${OnBoardingStatus.ProvisioningCompleted}`;
                          }
                      }                 
                      
                    }
                    return <>{status}</>                               
                  }
                }
                // sortable
              />
              <Column
                field="file"
                header={i18next.t('downloadTemplate')}
                style={{"display": 'table-cell'}}
                renderer={(cellData: any, rowData: any) => {
                  return (
                    <>
                      { rowData.data.type === ENode.Organisation && (hasFileDownloadPermission || isDownloadTemplateForAnOrgEnabled(rowData)) &&
                      <Button
                        id={rowData.data.id}
                        className="download-file"
                        type="inline"
                        content=""
                        onClick={() => {
                          downloadFile(rowData);
                        }}
                        icon={ <Icon                          
                          root="common" 
                          size="small" 
                          name="file-download" 
                        />}
                      />
                    }
                    </>
                  );
                }}
              />
            </HierarchyTable>
          </SplitPanelUtil.Table>

          {newOrganisationOpenState ? (
            <OrganisationHierarchyNewOrgSidePanel
              onClose={closeNewOrgSidepanel}
              onFinish={closeNewOrgSidepanelAndRefresh}
            />
          ) : null}
          {!!selectedNode ? (
            <OrganisationHierarchySidePanel
              onClose={closeSelectedTableNode}
              onFinish={closeSelectedTableNodeAndRefresh}
              selectedNode={selectedNode}
              fetchChildrenAndInsert={fetchChildrenAndInsert}
              isCurrencyDefaultValue={isCurrencyDefaultValue}
            />
          ) : null}
        </SplitPanelUtil>
      </div>
    </div>
  );
};

export default consumeAPIs(OrganisationHierarchy);
